import { useEffect, useRef, useState } from "react";
import { Manager, Socket } from "socket.io-client";
import { useAppSelector } from "src/store/hooks";
import { selectCurrentAccessToken } from "src/store/reducers/auth/authSlice";

const POLL_MS = 10_000;

/**
 * Joins the gateway room for a session and calls `onEvent` for every realtime hint. While the
 * socket is down the hook invokes `onPoll` every 10 s so the grid keeps refreshing from the server.
 */
export function useSessionSocket(
  sessionId: string | undefined,
  onEvent: (event: string, payload: any) => void,
  onPoll: () => void,
) {
  const token = useAppSelector(selectCurrentAccessToken);
  const [connected, setConnected] = useState(false);
  const handlers = useRef({ onEvent, onPoll });
  handlers.current = { onEvent, onPoll };

  useEffect(() => {
    if (!sessionId || !token) return;
    const url =
      process.env.REACT_APP_WEB_SOCKET_URL ||
      process.env.REACT_APP_API_BASE_URL ||
      "";
    const manager = new Manager(url, {
      extraHeaders: { authentication: `Bearer ${token}` },
      reconnection: true,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 5000,
    });
    // Browsers cannot set custom headers on the WebSocket upgrade; the token also rides in `auth`.
    const socket: Socket = manager.socket("/", { auth: { token } });
    let poll: ReturnType<typeof setInterval> | null = setInterval(
      () => handlers.current.onPoll(),
      POLL_MS,
    );
    const stopPoll = () => {
      if (poll) clearInterval(poll);
      poll = null;
    };
    const startPoll = () => {
      if (!poll) poll = setInterval(() => handlers.current.onPoll(), POLL_MS);
    };

    socket.on("connect", () => {
      setConnected(true);
      stopPoll();
      socket.emit("session:join", { sessionId });
      handlers.current.onPoll();
    });
    socket.on("disconnect", () => {
      setConnected(false);
      startPoll();
    });
    socket.on("connect_error", () => {
      setConnected(false);
      startPoll();
    });
    for (const event of [
      "session:entry",
      "session:status",
      "session:station",
      "session:student-status",
      "session:flag",
      "session:join-code",
      "session:documents",
    ]) {
      socket.on(event, (payload: any) => {
        if (payload?.sessionId === sessionId)
          handlers.current.onEvent(event, payload);
      });
    }

    return () => {
      stopPoll();
      socket.emit("session:leave", { sessionId });
      socket.removeAllListeners();
      socket.disconnect();
    };
  }, [sessionId, token]);

  return { connected };
}
